import { useCallback, useEffect, useRef, useState } from 'react'
import CampaignLayout from 'Layouts/CampaignLayout'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'
import {
    fetchRecommendedCreators,
    generateCreatorRecommendation,
    inviteCreatorsToCampaign,
} from 'services/api/campaign'
import { useGetCampaignDataById } from 'slices/campaigns/selector'
import { fetchCampaign } from 'slices/campaigns/thunk'
import {
    defaultRecommendedCreatorsFilters,
    updateRecommendedCreatorsFilters,
} from 'slices/campaigns/recommended-creators/reducer'
import { fetchRecommendedCreatorsThunk } from 'slices/campaigns/recommended-creators/thunk'
import AccessControl from 'wrappers/AccessControl'
import { PRIVILEGE } from 'constants/privilege'
import { AppDispatch, RootState } from 'store'
import { ERROR } from 'types'
import { generateUniqueKey, getErrorMessage } from 'utils/helpers'
import { getNextDiscoveryCreatorSort } from 'types/creator-discovery'
import DiscoveredCreatorsResults from 'Components/creator-discovery/DiscoveredCreatorsResults'
import { buildDiscoveredCreatorsCsv, downloadCsvFile } from 'utils/creatorDiscoveryCsvExport'
import 'pages/CreatorDiscovery/creator-discovery.scss'
import './recommended-creators.scss'
import withAccessControl from 'hoc/withAccessControl'
import { resetInvites } from 'slices/campaigns/invites/reducer'

const POLL_INTERVAL_MS = 5000
const MAX_POLL_DURATION_MS = 20 * 60 * 1000

const RecommendedCreators = () => {
    const { campaignId } = useParams()
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    const campaign = useGetCampaignDataById(campaignId!)
    const creatorRecommendationAllowed = campaign?.creator_recommendation_allowed ?? false;
    const recommendationInitiated = Boolean(campaign?.creator_recommendation_initiated)
    const recommendationsAvailable = Boolean(campaign?.creator_recommendation_available)

    const [isSubmitting, setIsSubmitting] = useState(false)
    const [pendingGeneration, setPendingGeneration] = useState(false)
    const [selectedCreatorIds, setSelectedCreatorIds] = useState<string[]>([])
    const [downloading, setDownloading] = useState(false)
    const [inviting, setInviting] = useState(false)
    const [initialLoading, setInitialLoading] = useState(true)
    const [filterId, setFilterId] = useState(
        generateUniqueKey(defaultRecommendedCreatorsFilters, ['page'])
    )

    const isGenerating =
        pendingGeneration || (recommendationInitiated && !recommendationsAvailable)

    const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const pollStartedAtRef = useRef<number | null>(null)

    const { filters, loading, error } = useSelector(
        (state: RootState) => state.campaignRecommendedCreators
    )
    const listData = useSelector(
        (state: RootState) =>
            state.campaignRecommendedCreators.campaigns[campaignId!]?.filtered_lists?.[filterId]
    )
    const creators = listData?.list ?? []
    const count = listData?.count ?? 0
    const page = listData?.page ?? 1
    const hasMore = listData?.has_more ?? true

    useEffect(() => {
        if (!creatorRecommendationAllowed) {
            navigate(`/campaign/${campaignId}/applications`)
        }
    }, [creatorRecommendationAllowed, campaignId, navigate])

    const stopPolling = useCallback(() => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current)
            pollIntervalRef.current = null
        }
        pollStartedAtRef.current = null
    }, [])

    useEffect(() => {
        if (recommendationInitiated || recommendationsAvailable) {
            setPendingGeneration(false)
        }
        if (recommendationsAvailable) {
            stopPolling()
        }
    }, [recommendationInitiated, recommendationsAvailable, stopPolling])

    useEffect(() => {
        stopPolling()

        if (!campaignId || !isGenerating || recommendationsAvailable) {
            return
        }

        pollStartedAtRef.current = Date.now()

        pollIntervalRef.current = setInterval(() => {
            if (
                pollStartedAtRef.current &&
                Date.now() - pollStartedAtRef.current >= MAX_POLL_DURATION_MS
            ) {
                stopPolling()
                setPendingGeneration(false)
                toast.info('Recommendations are taking longer than expected. Please check back later.')
                return
            }

            dispatch(fetchCampaign({ campaignId, force: true }))
        }, POLL_INTERVAL_MS)

        return stopPolling
    }, [campaignId, isGenerating, recommendationsAvailable, dispatch, stopPolling])

    useEffect(() => {
        if (!campaignId || !recommendationsAvailable) {
            setInitialLoading(false)
            return
        }

        if (filters.page === 1) {
            setInitialLoading(true)
        }

        dispatch(fetchRecommendedCreatorsThunk({ campaignId, filters }))
            .unwrap()
            .catch((err) => {
                if (err !== ERROR.CACHED) {
                    toast.error(typeof err === 'string' ? err : 'Failed to load recommended creators')
                }
            })
            .finally(() => {
                setFilterId(generateUniqueKey(filters, ['page']))
                setInitialLoading(false)
            })
    }, [campaignId, recommendationsAvailable, filters, dispatch])

    useEffect(() => {
        setSelectedCreatorIds([])
        dispatch(updateRecommendedCreatorsFilters(defaultRecommendedCreatorsFilters))
    }, [campaignId, dispatch])

    const generateRecommendations = async () => {
        if (!campaignId || isSubmitting || isGenerating) return

        setIsSubmitting(true)
        try {
            await generateCreatorRecommendation(campaignId)
            toast.success('Recommendation generation started')
            setPendingGeneration(true)
            dispatch(fetchCampaign({ campaignId, force: true }))
        } catch (err) {
            toast.error(getErrorMessage(err, 'Failed to generate recommendations'))
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleSortToggle = () => {
        const nextSort = getNextDiscoveryCreatorSort(filters.sort)
        setSelectedCreatorIds([])
        dispatch(updateRecommendedCreatorsFilters({ page: 1, sort: nextSort }))
    }

    const handleLoadMore = () => {
        if (!campaignId || !hasMore || loading) return
        dispatch(
            fetchRecommendedCreatorsThunk({
                campaignId,
                filters: { ...filters, page: page + 1 },
            })
        )
    }

    const handleToggleSelect = (creatorId: string) => {
        setSelectedCreatorIds((prev) =>
            prev.includes(creatorId) ? prev.filter((id) => id !== creatorId) : [...prev, creatorId]
        )
    }

    const handleClearSelection = () => setSelectedCreatorIds([])

    const handleDownload = async () => {
        if (!campaignId || downloading || count === 0) return

        setDownloading(true)
        try {
            const { items } = await fetchRecommendedCreators(campaignId, 1, filters.sort, count)
            const csv = buildDiscoveredCreatorsCsv(items)
            const date = new Date().toISOString().split('T')[0]
            downloadCsvFile(csv, `recommended_creators_${campaignId}_${date}.csv`)
            toast.success('Creators list downloaded successfully.')
        } catch (err) {
            toast.error(getErrorMessage(err, 'Failed to download creators'))
        } finally {
            setDownloading(false)
        }
    }

    const handleInvite = async () => {
        if (!campaignId || selectedCreatorIds.length === 0 || inviting) return

        const result = await Swal.fire({
            title: 'Invite selected creators?',
            text: `Invite ${selectedCreatorIds.length} creator${selectedCreatorIds.length === 1 ? '' : 's'} to this campaign?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Invite',
            cancelButtonText: 'Cancel',
            reverseButtons: true,
        })

        if (!result.isConfirmed) return

        setInviting(true)
        try {
            await inviteCreatorsToCampaign(campaignId, selectedCreatorIds)
            toast.success(
                `${selectedCreatorIds.length} creator${selectedCreatorIds.length === 1 ? '' : 's'} invited successfully.`
            )
            setSelectedCreatorIds([])
            dispatch(resetInvites({ campaignId }))
        } catch (err) {
            toast.error(getErrorMessage(err, 'Failed to invite creators'))
        } finally {
            setInviting(false)
        }
    }

    return (
        <CampaignLayout>
            {recommendationsAvailable ? (
                <div className="recommended-creators-results">
                    <DiscoveredCreatorsResults
                        title="Recommended Creators"
                        creators={creators}
                        count={count}
                        loading={initialLoading || (loading && creators.length === 0)}
                        sort={filters.sort}
                        selectedIds={selectedCreatorIds}
                        showSummary={false}
                        scrollableTarget="recommended-creators-scroll"
                        downloading={downloading}
                        inviting={inviting}
                        inviteLabel="Invite to Campaign"
                        inviteDisabledTooltip={
                            count === 0
                                ? 'No recommended creators found yet.'
                                : 'Select at least one creator to invite them to this campaign.'
                        }
                        emptyTitle="No recommended creators"
                        emptyText="We couldn't find recommended creators for this campaign yet. Try generating recommendations again later."
                        onSortToggle={handleSortToggle}
                        onToggleSelect={handleToggleSelect}
                        onClearSelection={handleClearSelection}
                        onLoadMore={handleLoadMore}
                        onDownload={handleDownload}
                        onInvite={handleInvite}
                    />
                    {error && creators.length > 0 && (
                        <div className="d-flex justify-content-center py-3">
                            <button
                                type="button"
                                className="theme-btn theme-btn-sm btn-primary-outlined"
                                onClick={handleLoadMore}
                            >
                                Load More <i className="ms-2 fs-5 bx bx-refresh" />
                            </button>
                        </div>
                    )}
                </div>
            ) : (
                <div className="recommended-creators-empty">
                    <div
                        className={`recommended-creators-empty__card${isGenerating ? ' recommended-creators-empty__card--generating' : ''}`}
                    >
                        <span className="recommended-creators-empty__badge">
                            <i className="ri-sparkling-2-line" aria-hidden />
                            AI Powered
                        </span>

                        {isGenerating ? (
                            <>
                                <div
                                    className="recommended-creators-empty__icon recommended-creators-empty__icon--spin"
                                    aria-hidden
                                >
                                    <i className="ri-loader-4-line" />
                                </div>
                                <h3 className="recommended-creators-empty__title">
                                    Generating recommendations
                                </h3>
                                <p className="recommended-creators-empty__text">
                                    Our AI is matching creators to this campaign brief. Hang tight — this usually takes a few minutes.
                                </p>
                                <div className="recommended-creators-empty__pulse" aria-hidden>
                                    <span className="recommended-creators-empty__pulse-dot" />
                                    <span className="recommended-creators-empty__pulse-dot" />
                                    <span className="recommended-creators-empty__pulse-dot" />
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="recommended-creators-empty__icon" aria-hidden>
                                    <i className="ri-user-search-line" />
                                </div>
                                <h3 className="recommended-creators-empty__title">
                                    No recommendations yet
                                </h3>
                                <p className="recommended-creators-empty__text">
                                    Generate an AI shortlist of creators who best fit this campaign&apos;s requirements and goals.
                                </p>
                                <AccessControl privilege={PRIVILEGE.INFLUENCER.INFLUENCER.AI_OPS}>
                                    <>
                                        <button
                                            type="button"
                                            className="theme-btn theme-btn-sm btn-gradient recommended-creators-empty__action"
                                            disabled={isSubmitting}
                                            onClick={generateRecommendations}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm" role="status" />
                                                    Starting...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="ri-sparkling-2-line" aria-hidden />
                                                    Generate Recommendations
                                                </>
                                            )}
                                        </button>
                                        <p className="recommended-creators-empty__hint">
                                            This usually takes a few minutes to complete
                                        </p>
                                    </>
                                </AccessControl>
                            </>
                        )}
                    </div>
                </div>
            )}
        </CampaignLayout>
    )
}

export default withAccessControl(RecommendedCreators, PRIVILEGE.INFLUENCER.INFLUENCER.AI_OPS)
